import * as Sentry from '@sentry/react';
import type { Dispatch } from '@reduxjs/toolkit';

import { isDeploymentUnreachableError, parseActor, serverMessageOf, statusOf } from './thunk';
import type {
  OfflineModeEvent,
  OfflineModeEventChain,
  OfflineModeEventChainFinding,
  OfflineModeEventDetail,
  OfflineModeEventLogErrorCode,
  OfflineModeEventPage,
  OfflineModeEventRetention,
  OfflineModeEventSource,
} from './index';
import TCClient from 'client';
import { actions } from 'redux-stores';
import { localServerRequestOptions } from 'common/utils/offlineMode/localServerAuthHeaders';

export const EVENT_LOG_PAGE_SIZE = 50;

export const eventLogPath = (organizationId: string, cursor?: string): string => {
  const base = `/v2/organization/${organizationId}/offline-mode/events?size=${EVENT_LOG_PAGE_SIZE}`;
  return cursor ? `${base}&page=${encodeURIComponent(cursor)}` : base;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const text = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const count = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : undefined;

const timestamp = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

export function parseEventSource(raw: unknown): OfflineModeEventSource {
  if (!isRecord(raw)) return {};
  const source: OfflineModeEventSource = {};
  const node = text(raw.node);
  if (node) source.node = node;
  const side = text(raw.side);
  if (side) source.side = side;
  const producer = text(raw.producer);
  if (producer) source.producer = producer;
  return source;
}

function parseRecipientScope(raw: unknown): OfflineModeEventDetail['recipientScope'] {
  if (!isRecord(raw)) return undefined;
  const scope: NonNullable<OfflineModeEventDetail['recipientScope']> = {};
  const kind = text(raw.kind);
  if (kind) scope.kind = kind;
  const orgToken = text(raw.org_token);
  if (orgToken) scope.orgToken = orgToken;
  const resolvedCount = count(raw.resolved_count);
  if (resolvedCount !== undefined) scope.resolvedCount = resolvedCount;
  return Object.keys(scope).length ? scope : undefined;
}

export function parseEventDetail(raw: unknown): OfflineModeEventDetail {
  if (!isRecord(raw)) return {};
  const detail: OfflineModeEventDetail = {};

  const state = text(raw.state);
  if (state) detail.state = state;
  const version = count(raw.version);
  if (version !== undefined) detail.version = version;
  const origin = text(raw.origin);
  if (origin) detail.origin = origin;

  const phase = text(raw.phase);
  if (phase) detail.phase = phase;
  const reason = text(raw.reason);
  if (reason) detail.reason = reason;

  const body = text(raw.body);
  if (body) detail.body = body;
  const recipientScope = parseRecipientScope(raw.recipient_scope);
  if (recipientScope) detail.recipientScope = recipientScope;
  const template = text(raw.template);
  if (template) detail.template = template;
  const direction = text(raw.direction);
  if (direction) detail.direction = direction;

  const path = text(raw.path);
  if (path) detail.path = path;
  const previousPath = text(raw.previous_path);
  if (previousPath) detail.previousPath = previousPath;

  const status = text(raw.status);
  if (status) detail.status = status;
  const publishedVersion = count(raw.published_version);
  if (publishedVersion !== undefined) detail.publishedVersion = publishedVersion;
  const target = text(raw.target);
  if (target) detail.target = target;
  const note = text(raw.detail);
  if (note) detail.note = note;

  return detail;
}

export function parseEvent(raw: unknown): OfflineModeEvent | null {
  if (!isRecord(raw)) return null;

  const seq = count(raw.seq);
  const eventType = text(raw.event_type);
  if (seq === undefined && !eventType) return null;

  const event: OfflineModeEvent = {
    seq: seq ?? 0,
    eventType: eventType ?? '',
    occurredAt: timestamp(raw.occurred_at) ?? 0,
    actorKnown: raw.actor_known === true,
    source: parseEventSource(raw.source),
    detail: parseEventDetail(raw.detail),
    annotations: Array.isArray(raw.annotations)
      ? raw.annotations.filter((a): a is string => typeof a === 'string' && !!a.trim())
      : [],
  };

  const eventId = text(raw.event_id);
  if (eventId) event.eventId = eventId;
  const recordedAt = timestamp(raw.recorded_at);
  if (recordedAt) event.recordedAt = recordedAt;
  const actorRaw = isRecord(raw.actor) ? raw.actor : undefined;
  const actor = parseActor(
    actorRaw ? { token: text(actorRaw.token), name: text(actorRaw.name) } : undefined
  );
  if (actor) event.actor = actor;
  const prevHash = text(raw.prev_hash);
  if (prevHash) event.prevHash = prevHash;
  const hash = text(raw.hash);
  if (hash) event.hash = hash;

  return event;
}

export function parseEventChain(raw: unknown): OfflineModeEventChain | null {
  if (!isRecord(raw)) return null;

  const findings: OfflineModeEventChainFinding[] = Array.isArray(raw.findings)
    ? raw.findings.flatMap((entry) => {
        if (!isRecord(entry)) return [];
        const type = text(entry.type);
        if (!type) return [];
        const finding: OfflineModeEventChainFinding = { type };
        const seq = count(entry.seq);
        if (seq !== undefined) finding.seq = seq;
        return [finding];
      })
    : [];

  const chain: OfflineModeEventChain = {
    checked: count(raw.checked) ?? 0,
    intact: raw.intact === true,
    anchored: raw.anchored === true,
    findings,
  };
  const scope = text(raw.scope);
  if (scope) chain.scope = scope;
  return chain;
}

export function parseEventRetention(raw: unknown): OfflineModeEventRetention | null {
  if (!isRecord(raw)) return null;
  const retention: OfflineModeEventRetention = {};
  const days = count(raw.days);
  if (days !== undefined) retention.days = days;
  const configKey = text(raw.config_key);
  if (configKey) retention.configKey = configKey;
  const status = text(raw.status);
  if (status) retention.status = status;
  const exported = text(raw.export);
  if (exported) retention.export = exported;
  const note = text(raw.note);
  if (note) retention.note = note;
  return Object.keys(retention).length ? retention : null;
}

export function parseAnnotationText(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isRecord(raw)) return out;
  Object.entries(raw).forEach(([code, value]) => {
    const sentence = text(value);
    if (code.trim() && sentence) out[code] = sentence;
  });
  return out;
}

export function parseEventPage(raw: unknown, append: boolean): OfflineModeEventPage | null {
  if (!isRecord(raw)) return null;
  if (!Array.isArray(raw.events)) return null;

  const events: OfflineModeEvent[] = [];
  let unreadable = 0;
  raw.events.forEach((row) => {
    const event = parseEvent(row);
    if (event) events.push(event);
    else unreadable += 1;
  });

  const metadata = isRecord(raw.metadata) ? raw.metadata : {};
  const nextPage = text(metadata.page) ?? null;

  return {
    events,
    chain: parseEventChain(raw.chain),
    retention: parseEventRetention(raw.retention),
    annotationText: {
      ...parseAnnotationText(raw.annotation_text),
      ...parseAnnotationText(raw.annotations_text),
    },
    nextPage,
    isFullyFetched: metadata.is_fully_fetched === true || !nextPage,
    unreadable,
    append,
  };
}

export function eventLogErrorCode(err: unknown): OfflineModeEventLogErrorCode {
  if (isDeploymentUnreachableError(err)) return 'unreachable';
  return statusOf(err) === 403 ? 'forbidden' : 'read-failed';
}

export type FetchOfflineModeEventLogOptions = {
  organizationId: string;
  cursor?: string;
};

export const fetchOfflineModeEventLog = async (
  dispatch: Dispatch,
  { organizationId, cursor }: FetchOfflineModeEventLogOptions
) => {
  if (!organizationId) return;

  const isMore = !!cursor;
  dispatch(actions.setOfflineModeEventsLoading({ isMore }));

  try {
    const res = await TCClient.httpClient.get(
      eventLogPath(organizationId, cursor),
      localServerRequestOptions(TCClient.httpClient)
    );
    const page = parseEventPage(res.data, isMore);

    if (!page) {
      Sentry.logger.error(
        Sentry.logger.fmt`[OfflineMode] Unreadable event log page for ${organizationId}`
      );
      dispatch(actions.setOfflineModeEventsFailed({ code: 'unusable' }, organizationId));
      return;
    }

    dispatch(actions.setOfflineModeEvents(page, organizationId));
  } catch (err: unknown) {
    Sentry.logger.error(Sentry.logger.fmt`[OfflineMode] Failed to read the event log page`, {
      err,
    });
    const code = eventLogErrorCode(err);
    const serverMessage = serverMessageOf(err);
    dispatch(
      actions.setOfflineModeEventsFailed(
        serverMessage ? { code, serverMessage } : { code },
        organizationId
      )
    );
  }
};
